import { ReactNode } from 'react';

import { DateRange } from '@mui/x-date-pickers-pro';

import {
  InferenceVolumeByStatusCodeDatapoint,
  InferenceVolumeByStatusDatapoint,
  ResponseTimeDatapoint,
  TransportKind,
} from '@/graphql';
import { TypedDocumentNode } from '@apollo/client';
import { Moment } from 'moment';

export type MetricsProps = {
  gpuCount?: number;
  canUseInferenceVolumeByStatus: boolean;
  id?: string;
  created?: string;
  versionsSwitcher?: ReactNode;
  transportKind?: TransportKind;
  speculativeDecodingMetricsEnabled?: boolean;
  defaultDateRange?: DateRange<Moment>;
};

export type DragParams = {
  index?: number;
  isDragging: boolean;
  id: string;
  dragOverlay: boolean;
};

export type MetricsChartProps = {
  canUseInferenceVolumeByStatus: boolean;
  dragParams: DragParams;
  transportKind?: TransportKind;
  hide: (chart: AnyChart) => void;
  chartId: AnyChart;
};

export type TimeRange = [Date, Date];

export enum RelativeTimeRange {
  LAST_10_MINUTES = 'Last 10 minutes',
  LAST_1_HOUR = 'Last 1 hour',
  LAST_6_HOURS = 'Last 6 hours',
  LAST_24_HOURS = 'Last 24 hours',
  LAST_3_DAYS = 'Last 3 days',
  LAST_7_DAYS = 'Last 7 days',
}

export type QueryVariables = {
  modelVersionId: string;
  start: string;
  end: string;
  stepSeconds?: number;
  timeDivisorSeconds: number;
};

export type QueryVariablesSharedEndpoint = {
  orgSharedEndpointId: string;
  start: string;
  end: string;
  stepSeconds?: number;
  timeDivisorSeconds: number;
};

export type QueryVariablesTrainingJob = {
  trainingJobId: string;
  start: string;
  end: string;
};

export type BaseDatapoint = {
  /**
   * ISO 8601 timestamp
   */
  timestamp: string;
};

export type LabelFormatter = (timeDivisorUnit: string) => string;

export type ValueFormatter = (value: number, timeDivisorUnit?: string) => ReactNode;

export enum StatusCodeColor {
  Primary = 'primary',
  Secondary = 'secondary',
  Success = 'success',
  Error = 'error',
  Warning = 'warning',
  Info = 'info',
}

export type BaseDatasetDefinition = {
  key: string;
  label: string | LabelFormatter;
  color: StatusCodeColor | string;
  icon?: JSX.Element;
  valueFormatter?: ValueFormatter;
};

export type MetricQueryHookAPI<T> = {
  /**
   * The latest available data, or null if no data is available. We will continue to show the last data
   * after the time range has changed until new data is available.
   */
  data: T | null;
  inputVariables: QueryVariables | QueryVariablesSharedEndpoint | QueryVariablesTrainingJob;
  inputRelativeTimeRange: RelativeTimeRange;
  loading: boolean;
  error: unknown;
};

export type RESPONSE_TIME_METRIC_KEY = Exclude<
  keyof ResponseTimeDatapoint,
  '__typename' | 'timestamp'
>;

export type PERCENTILE_KEY = Exclude<
  keyof ResponseTimeDatapoint,
  '__typename' | 'timestamp' | 'avg'
>;

export type STATUS_KEY = Exclude<
  keyof InferenceVolumeByStatusDatapoint,
  '__typename' | 'timestamp'
>;

export type HTTP_STATUS_CODE_KEY = Exclude<
  keyof InferenceVolumeByStatusCodeDatapoint,
  '__typename' | 'timestamp'
>;

export enum ResponseTimeMode {
  EndToEndLatency = 'end-to-end',
  InferenceTime = 'inference-time',
}

export enum Section {
  Overview = 'overview',
  Async = 'async',
}

export enum Chart {
  InferenceVolume = 'inference-volume',
  EndToEndLatency = 'end-to-end-latency',
  InferenceTime = 'inference-time',
  OngoingWebsocketConnections = 'ongoing-websocket-connections',
  RequestSize = 'request-size',
  ResponseSize = 'response-size',
  TimeToFirstByte = 'time-to-first-byte',
  ReplicaCount = 'replica-count',
  CpuUsage = 'cpu-usage',
  CpuMemory = 'cpu-memory',
  GpuUsage = 'gpu-usage',
  GpuMemory = 'gpu-memory',
  TimeInAsyncQueue = 'time-in-async-queue',
  AsyncQueueSize = 'async-queue-size',
  KVCacheHitRate = 'kv-cache-hit-rate',
  SpeculationRate = 'speculation-rate',
}

export enum SharedEndpointChart {
  InferenceVolume = 'inference-volume',
  RateLimitVolume = 'rate-limit-volume',
  RequestSize = 'request-size',
  ResponseSize = 'response-size',
  TimeToFirstByte = 'time-to-first-byte',
  TokensUsage = 'tokens-usage',
}

export enum TrainingJobChart {
  GpuMemoryUsageBytes = 'gpu-memory-usage-bytes',
  GpuUtilization = 'gpu-utilization',
  CpuUsage = 'cpu-usage',
  MemoryUsageBytes = 'memory-usage-bytes',
  EphemeralStorageUsageBytes = 'ephemeral-storage-usage-bytes',
  ReadWriteCacheUsageBytes = 'read-write-cache-usage-bytes',
}

export type AnyChart = Chart | SharedEndpointChart | TrainingJobChart;

export type ChartConfig = {
  id: AnyChart;
  hidden: boolean;
};

export type ContentProps = {
  showGpuCharts: boolean;
  canUseInferenceVolumeByStatus: boolean;
  openCustomizeMetrics: () => void;
  transportKind?: TransportKind;
  speculativeDecodingMetricsEnabled?: boolean;
};

export enum HTTPStatus {
  Status2xx = 'status_2xx',
  Status4xx = 'status_4xx',
  Status5xx = 'status_5xx',
}

export enum HTTPStatusCode {
  OK = 200,
  ACCEPTED = 202,
  BAD_REQUEST = 400,
  NOT_FOUND = 404,
  UNPROCESSABLE_ENTITY = 422,
  FAILED_DEPENDENCY = 424,
  CLIENT_CLOSED_REQUEST = 499,
  INTERNAL_SERVER_ERROR = 500,
  BAD_GATEWAY = 502,
  SERVICE_UNAVAILABLE = 503,
  GATEWAY_TIMEOUT = 504,
}

export enum GRPCStatusCode {
  OK = 0,
  CANCELLED = 1,
  UNKNOWN = 2,
  INVALID_ARGUMENT = 3,
  DEADLINE_EXCEEDED = 4,
  NOT_FOUND = 5,
  ALREADY_EXISTS = 6,
  PERMISSION_DENIED = 7,
  RESOURCE_EXHAUSTED = 8,
  FAILED_PRECONDITION = 9,
  ABORTED = 10,
  OUT_OF_RANGE = 11,
  UNIMPLEMENTED = 12,
  INTERNAL = 13,
  UNAVAILABLE = 14,
  DATA_LOSS = 15,
  UNAUTHENTICATED = 16,
}

export enum WebsocketStatusCode {
  NORMAL_CLOSURE = 1000,
  GOING_AWAY = 1001,
  PROTOCOL_ERROR = 1002,
  UNSUPPORTED_DATA = 1003,
  NO_STATUS_RECEIVED = 1005,
  ABNORMAL_CLOSURE = 1006,
  INVALID_FRAME_PAYLOAD_DATA = 1007,
  POLICY_VIOLATION = 1008,
  MESSAGE_TOO_BIG = 1009,
  MANDATORY_EXT = 1010,
  INTERNAL_ERROR = 1011,
  SERVICE_RESTART = 1012,
  TRY_AGAIN_LATER = 1013,
  BAD_GATEWAY = 1014,
  TLS_HANDSHAKE = 1015,
}

export type StatusCodeType<T extends TransportKind> = T extends TransportKind.Grpc
  ? GRPCStatusCode
  : T extends TransportKind.Websocket
  ? WebsocketStatusCode
  : never;

export type GpuItem = { name: string; datapoints: { timestamp: any; value: number }[] };

export type UseQueryVariablesArgs = {
  id: string;
  isSharedEndpoint: boolean;
  isTrainingJob: boolean;
  timeDivisorSeconds?: number;
};

export type UseMetricQueryArgs<T> = {
  document: TypedDocumentNode<T, QueryVariables | QueryVariablesSharedEndpoint>;
  isSharedEndpoint: boolean;
  isTrainingJob: boolean;
  isVisible?: boolean;
  providerId?: string;
  timeDivisorSeconds?: number;
};

export type GetAvailableMetricsConfigArgs = {
  config: ChartConfig[];
  showGpuCharts: boolean;
  transportKind: TransportKind;
  shouldFilterOutNonHttpTransportMetrics: boolean;
  speculativeDecodingMetricsEnabled: boolean;
};

export type SelectedMetricsUnits = {
  header: string[];
  legends: string[];
  hoveredLegends: string[];
};
